import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import classNames from "classnames";
import { type LucideIcon } from "lucide-react";
import { ChevronRight } from "lucide-react";

export type MenuItemVariant = "default" | "danger";

export interface MenuItemButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: LucideIcon;
  label: string;
  description?: string;
  trailing?: ReactNode;
  /** Exibe chevron à direita quando true (padrão para itens de navegação). */
  showChevron?: boolean;
  variant?: MenuItemVariant;
}

const variantClasses: Record<MenuItemVariant, { icon: string; label: string }> = {
  default: { icon: "text-text-muted",  label: "text-text-main" },
  danger:  { icon: "text-red-400",     label: "text-red-400" },
};

/**
 * MenuItemButton — item de ação em sheets e context menus.
 * Usar para: listas de ações ("Editar", "Apagar", "Compartilhar", configurações).
 *
 * icon        → ícone Lucide à esquerda (opcional)
 * label       → texto principal (obrigatório)
 * description → texto auxiliar abaixo do label (opcional)
 * trailing    → elemento à direita (badge, toggle, etc.)
 * showChevron → chevron "›" à direita (padrão false)
 * variant     → "default" | "danger"
 */
export const MenuItemButton = forwardRef<HTMLButtonElement, MenuItemButtonProps>(
  function MenuItemButton(
    { icon: Icon, label, description, trailing, showChevron = false, variant = "default", className, ...props },
    ref,
  ) {
    const colors = variantClasses[variant];

    return (
      <button
        ref={ref}
        type="button"
        className={classNames(
          "flex w-full items-center gap-3 px-4 py-3",
          "font-body text-sm transition-colors duration-150",
          "hover:bg-surface-alt/60 active:bg-surface-alt",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40",
          "disabled:opacity-40 disabled:cursor-not-allowed",
          className,
        )}
        {...props}
      >
        {Icon && (
          <span className={classNames("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-surface-alt", colors.icon)}>
            <Icon size={18} aria-hidden />
          </span>
        )}
        <span className="min-w-0 flex-1 text-left">
          <span className={classNames("block truncate font-semibold leading-tight", colors.label)}>
            {label}
          </span>
          {description && (
            <span className="block truncate text-[12px] font-normal leading-snug text-text-muted">
              {description}
            </span>
          )}
        </span>
        {trailing && <span className="shrink-0">{trailing}</span>}
        {showChevron && !trailing && (
          <ChevronRight size={16} className="shrink-0 text-text-muted" aria-hidden />
        )}
      </button>
    );
  },
);

MenuItemButton.displayName = "MenuItemButton";
